import { createApiClient } from '@enderrealm/api-sdk';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as storage from './storage';
import type {
  EnvironmentConfig,
  EnvironmentHealth,
} from './types';
import {
  HEALTH_POLL_MS,
  HEALTH_TIMEOUT_MS,
  makeId,
  normalizeBaseUrl,
  parseBuiltinEnvironments,
} from './utils';

export interface AddEnvironmentInput {
  name: string;
  baseUrl: string;
  description?: string;
  apiKey?: string;
}

export interface EditEnvironmentPatch {
  name?: string;
  baseUrl?: string;
  description?: string;
}

interface EnvironmentContextValue {
  environments: EnvironmentConfig[];
  /** 按 envId 索引的健康状态 */
  healthMap: Record<string, EnvironmentHealth>;
  activeId: string | null;
  activeEnvironment?: EnvironmentConfig;
  apiKeys: Record<string, string>;
  setActiveEnv: (id: string) => void;
  setApiKey: (envId: string, apiKey: string) => void;
  addEnvironment: (input: AddEnvironmentInput) => EnvironmentConfig;
  updateEnvironment: (envId: string, patch: EditEnvironmentPatch) => void;
  removeEnvironment: (envId: string) => void;
  refreshHealth: (envId?: string) => void;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  /** Master Key（仅存于内存，不持久化；API Key 管理页使用） */
  masterKey: string;
  setMasterKey: (key: string) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue | null>(null);

function errorDetail(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail) return detail;
  }
  return '服务状态异常';
}

export function EnvironmentProvider({ children }: { children: React.ReactNode }) {
  const [localEnvs, setLocalEnvs] = useState<EnvironmentConfig[]>(() =>
    storage.loadLocalEnvironments(),
  );
  const [apiKeys, setApiKeys] = useState<Record<string, string>>(() => storage.loadApiKeys());
  const [activeId, setActiveIdState] = useState<string | null>(() => storage.loadActiveId());
  const [healthMap, setHealthMap] = useState<Record<string, EnvironmentHealth>>({});
  const [panelOpen, setPanelOpen] = useState(false);
  const [masterKey, setMasterKeyState] = useState('');
  const setMasterKey = useCallback((key: string) => setMasterKeyState(key), []);

  const builtinEnvs = useMemo(() => parseBuiltinEnvironments(), []);
  const environments = useMemo(
    () => [...builtinEnvs, ...localEnvs],
    [builtinEnvs, localEnvs],
  );

  const environmentsRef = useRef(environments);
  environmentsRef.current = environments;
  const apiKeysRef = useRef(apiKeys);
  apiKeysRef.current = apiKeys;
  const checkSequence = useRef(0);

  // 当前环境无效或为空时，回退到第一个环境
  useEffect(() => {
    const valid = environments.some((env) => env.id === activeId);
    if (!valid) {
      const fallback = environments[0]?.id ?? null;
      setActiveIdState(fallback);
      storage.saveActiveId(fallback);
    }
  }, [activeId, environments]);

  const updateHealth = useCallback((envId: string, health: EnvironmentHealth) => {
    setHealthMap((prev) => ({ ...prev, [envId]: health }));
  }, []);

  const runHealthCheck = useCallback(
    (env: EnvironmentConfig) => {
      const seq = ++checkSequence.current;
      updateHealth(env.id, { status: 'checking' });
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
      const started = Date.now();
      const apiKey = apiKeysRef.current[env.id] ?? '';
      (async () => {
        try {
          const client = createApiClient({ baseUrl: env.baseUrl, apiKey });
          const { data, error } = await client.GET('/v1/health/', {
            signal: controller.signal,
          });
          const latencyMs = Date.now() - started;
          if (checkSequence.current !== seq) return;
          if (data && data.status === 'OK') {
            updateHealth(env.id, { status: 'online', checkedAt: Date.now(), latencyMs });
          } else {
            updateHealth(env.id, {
              status: 'offline',
              checkedAt: Date.now(),
              latencyMs,
              detail: error ? errorDetail(error) : undefined,
            });
          }
        } catch (err) {
          if (checkSequence.current !== seq) return;
          const isAbort =
            typeof DOMException !== 'undefined' &&
            err instanceof DOMException &&
            err.name === 'AbortError';
          updateHealth(env.id, {
            status: 'offline',
            checkedAt: Date.now(),
            detail: isAbort
              ? '请求超时'
              : err instanceof Error
                ? err.message
                : String(err),
          });
        } finally {
          clearTimeout(timer);
        }
      })();
    },
    [updateHealth],
  );

  const refreshHealth = useCallback(
    (envId?: string) => {
      const targets = envId
        ? environmentsRef.current.filter((env) => env.id === envId)
        : environmentsRef.current;
      targets.forEach(runHealthCheck);
    },
    [runHealthCheck],
  );

  // 环境列表变化时立即检查，并每 30s 轮询
  useEffect(() => {
    refreshHealth();
    const timer = setInterval(refreshHealth, HEALTH_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshHealth, environments]);

  const setActiveEnv = useCallback(
    (id: string) => {
      setActiveIdState(id);
      storage.saveActiveId(id);
      refreshHealth(id);
    },
    [refreshHealth],
  );

  const setApiKey = useCallback(
    (envId: string, apiKey: string) => {
      const trimmed = apiKey.trim();
      const next = { ...apiKeysRef.current };
      if (trimmed) next[envId] = trimmed;
      else delete next[envId];
      apiKeysRef.current = next;
      setApiKeys(next);
      storage.saveApiKeys(next);
      refreshHealth(envId);
    },
    [refreshHealth],
  );

  const addEnvironment = useCallback((input: AddEnvironmentInput): EnvironmentConfig => {
    const existing = environmentsRef.current.map((env) => env.id);
    const env: EnvironmentConfig = {
      id: makeId(input.name, 'local', existing),
      name: input.name.trim(),
      description: input.description?.trim() || undefined,
      baseUrl: normalizeBaseUrl(input.baseUrl),
      source: 'local',
    };
    setLocalEnvs((prev) => {
      const list = [...prev, env];
      storage.saveLocalEnvironments(list);
      return list;
    });
    const apiKey = input.apiKey?.trim();
    if (apiKey) {
      const keys = { ...apiKeysRef.current, [env.id]: apiKey };
      apiKeysRef.current = keys;
      setApiKeys(keys);
      storage.saveApiKeys(keys);
    }
    setActiveIdState(env.id);
    storage.saveActiveId(env.id);
    return env;
  }, []);

  const updateEnvironment = useCallback((envId: string, patch: EditEnvironmentPatch) => {
    setLocalEnvs((prev) => {
      const next = prev.map((env) => {
        if (env.id !== envId) return env;
        const baseUrl = patch.baseUrl === undefined ? env.baseUrl : normalizeBaseUrl(patch.baseUrl);
        return {
          ...env,
          name: patch.name?.trim() || env.name,
          description: patch.description?.trim() || undefined,
          baseUrl,
        };
      });
      storage.saveLocalEnvironments(next);
      return next;
    });
  }, []);

  const removeEnvironment = useCallback((envId: string) => {
    setApiKeys((prev) => {
      const next = { ...prev };
      delete next[envId];
      storage.saveApiKeys(next);
      return next;
    });
    setHealthMap((prev) => {
      const next = { ...prev };
      delete next[envId];
      return next;
    });
    setLocalEnvs((prev) => {
      const next = prev.filter((env) => env.id !== envId);
      storage.saveLocalEnvironments(next);
      return next;
    });
  }, []);

  const activeEnvironment = environments.find((env) => env.id === activeId);

  const value: EnvironmentContextValue = {
    environments,
    healthMap,
    activeId,
    activeEnvironment,
    apiKeys,
    setActiveEnv,
    setApiKey,
    addEnvironment,
    updateEnvironment,
    removeEnvironment,
    refreshHealth,
    panelOpen,
    openPanel: () => setPanelOpen(true),
    closePanel: () => setPanelOpen(false),
    masterKey,
    setMasterKey,
  };

  return (
    <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>
  );
}

export function useEnvironment(): EnvironmentContextValue {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) {
    throw new Error('useEnvironment 必须在 <EnvironmentProvider> 内使用');
  }
  return ctx;
}

export default EnvironmentProvider;